import React from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/button';

const PayoutDetailsModal = ({ isOpen, onClose, payoutDetails }) => {
  if (!isOpen || !payoutDetails) return null;

  const { payout, task_breakdown } = payoutDetails;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Payout Details</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Payout #{payout.payout_number}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Payout Summary */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-6 text-white mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold">{payout.tutor_name}</h3>
              <p className="text-sm text-green-100">
                {new Date(payout.period_start).toLocaleDateString()} - {new Date(payout.period_end).toLocaleDateString()}
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold">₹{payout.final_amount.toLocaleString()}</p>
              <p className="text-sm text-green-100">Final Amount</p>
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-green-400">
            <div>
              <p className="text-sm text-green-100">Base Amount</p>
              <p className="text-lg font-bold">₹{payout.amount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-green-100">Adjustments</p>
              <p className="text-lg font-bold text-blue-200">+₹{payout.adjustments.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-sm text-green-100">Deductions</p>
              <p className="text-lg font-bold text-red-200">-₹{payout.deductions.toLocaleString()}</p>
            </div>
          </div>
        </div>

        {/* Task Breakdown */}
        <div className="space-y-4">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Task Breakdown</h3>
          
          {task_breakdown && task_breakdown.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Task Name</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Hours</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Rate</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-900 dark:text-white">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {task_breakdown.map((task, index) => (
                    <tr key={index} className="border-b border-gray-100 dark:border-gray-700">
                      <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">{task.task_name}</td>
                      <td className="py-3 px-4 text-right">{task.hours_worked.toFixed(1)}h</td>
                      <td className="py-3 px-4 text-right">₹{task.payout_rate}/hr</td>
                      <td className="py-3 px-4 text-right font-semibold text-green-600">₹{task.total_amount.toFixed(2)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-bold">
                    <td className="py-3 px-4">Total</td>
                    <td className="py-3 px-4 text-right">
                      {task_breakdown.reduce((sum, t) => sum + t.hours_worked, 0).toFixed(1)}h
                    </td>
                    <td className="py-3 px-4"></td>
                    <td className="py-3 px-4 text-right text-green-600">
                      ₹{task_breakdown.reduce((sum, t) => sum + t.total_amount, 0).toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4">No task breakdown available</p>
          )}
        </div>

        {/* Status and Description */}
        <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Status</p>
              <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                {payout.status}
              </span>
            </div>
            {payout.payout_date && (
              <div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Payout Date</p>
                <p className="text-gray-900 dark:text-white">{new Date(payout.payout_date).toLocaleDateString()}</p>
              </div>
            )}
          </div>
          
          {payout.description && (
            <div className="mt-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Description</p>
              <p className="text-gray-900 dark:text-white">{payout.description}</p>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PayoutDetailsModal;
